import { useVerifyIsMobile } from 'hooks/useVerifyIsMobile';
import React, { useEffect, useRef, useState } from 'react';

const useLazyLoadCarousels = ({
  preloadNumber,
  carousels,
  isLoadingCarousels,
  pace,
}) => {
  const [loadedCarousels, setLoadedCarousels] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const observerExecutedRef = useRef(false);
  const sentinelRef = useRef(null);
  const { isMobile } = useVerifyIsMobile(917);

  const loadPage = (startPageIndex, pageCount) => {
    setLoading(true);
    setTimeout(() => {
      const newPages = carousels.slice(
        startPageIndex,
        startPageIndex + pageCount,
      );
      setLoadedCarousels(prev => [...prev, ...newPages]);
      setLoading(false);
      const lastPageIndex = startPageIndex + pageCount - 1;
      if (lastPageIndex >= carousels.length - 1) setHasMore(false);
    }, 0);
  };

  const loadInitialContent = () => {
    for (let i = 0; i < preloadNumber; i++) {
      loadPage(i, 1);
    }
    setInitialLoadComplete(true);
  };

  useEffect(
    () => {
      if (isLoadingCarousels) {
        setInitialLoadComplete(false);
        setLoadedCarousels([]);
      }
    },
    [isLoadingCarousels],
  );

  useEffect(
    () => {
      if (!carousels?.length) return;

      if (
        !isLoadingCarousels &&
        !initialLoadComplete &&
        loadedCarousels?.length === 0
      )
        loadInitialContent();
    },
    [
      carousels,
      isLoadingCarousels,
      loadedCarousels,
      carousels?.length,
      initialLoadComplete,
    ],
  );

  useEffect(
    () => {
      const observer = new IntersectionObserver(
        entries => {
          const [entry] = entries;
          if (
            entry.isIntersecting &&
            !loading &&
            hasMore &&
            initialLoadComplete &&
            observerExecutedRef.current
          ) {
            loadPage(loadedCarousels.length, pace);
          }
          observerExecutedRef.current = true;
        },
        {
          rootMargin: isMobile ? '-100px' : '200px',
        },
      );

      if (sentinelRef.current) {
        observer.observe(sentinelRef.current);
      }

      return () => {
        if (sentinelRef.current) {
          observer.unobserve(sentinelRef.current);
        }
      };
    },
    [loadedCarousels, loading, hasMore, initialLoadComplete, isMobile],
  );

  const sentinelDiv = () => (
    <div ref={sentinelRef} style={{ height: '20px', order: 4 }} />
  );

  return {
    loadedCarousels,
    sentinelRef,
    sentinelDiv,
    loading,
  };
};

export default useLazyLoadCarousels;
